import React from 'react';
import styled, { css } from 'styled-components';
import {
  FloatingLink,
  LaunchIcon,
  LinkIcon,
  LinkOffIcon,
  ShortTextIcon,
  useFloatingLinkSelectors,
} from '@udecode/plate-link';
import { Tooltip } from 'src/presentation/components/agent_desktop_components';
import { FloatingIconWrapper, FloatingInputWrapper, FloatingVerticalDivider } from '@udecode/plate-ui-toolbar';

export const EditorFloatingLink = (): JSX.Element | null => {
  const isEditing = useFloatingLinkSelectors().isEditing();

  const input = (
    <StyledInputWrapper>
      <FloatingInputWrapper>
        <FloatingIconWrapper>
          <LinkIcon width={18} />
        </FloatingIconWrapper>

        <StyledFloatingUrlInput placeholder="https://www.example.com" />
      </FloatingInputWrapper>

      <StyledDivider />
      <FloatingInputWrapper>
        <FloatingIconWrapper>
          <ShortTextIcon width={18} />
        </FloatingIconWrapper>
        <StyledFloatingTextInput placeholder="Text to display" />
      </FloatingInputWrapper>
    </StyledInputWrapper>
  );

  const editContent = !isEditing ? (
    <StyledFloatingRow>
      <StyledEditButton>Edit link</StyledEditButton>

      <FloatingVerticalDivider />

      <Tooltip leaveDelay={300} data-aid="column-tooltip" message="Open link" style={{ left: '2px' }}>
        <StyledOpenLinkButton>
          <LaunchIcon width={18} />
        </StyledOpenLinkButton>
      </Tooltip>

      <FloatingVerticalDivider />

      <Tooltip leaveDelay={300} data-aid="column-tooltip" message="Unlink" style={{ left: '2px' }}>
        <StyledUnlinkButton>
          <LinkOffIcon width={18} />
        </StyledUnlinkButton>
      </Tooltip>
    </StyledFloatingRow>
  ) : (
    input
  );

  return (
    <>
      <StyledInsertRoot>{input}</StyledInsertRoot>
      <StyledEditRoot>{editContent}</StyledEditRoot>
    </>
  );
};

const StyledInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 330px;
`;

const StyledDivider = styled.div`
  height: 1px;
  background-color: ${p => p.theme.gray200};
`;

const CSSFloatingButton = css`
  align-items: center;
  display: inline-flex;
  justify-content: center;
  max-width: 100%;
  padding: 0.25rem 0.625rem;
  position: relative;
  text-align: center;
  border-width: 0px;
  cursor: pointer;
  font-weight: 500;
  font-family: inherit;
  font-size: 14px;
  border-radius: 3px;
  background-color: ${p => p.theme.colors.white};

  &:active {
    background-color: ${p => p.theme.colors.gray500};
  }

  svg {
    fill: ${p => p.theme.colors.black};
  }

  &:hover {
    background-color: ${p => p.theme.colors.gray200};
  }
`;

const StyledEditButton = styled(FloatingLink.EditButton)`
  ${CSSFloatingButton};
`;

const StyledUnlinkButton = styled(FloatingLink.UnlinkButton)`
  ${CSSFloatingButton}
`;

const StyledOpenLinkButton = styled(FloatingLink.OpenLinkButton)`
  ${CSSFloatingButton}
`;

const CSSFloatingRoot = css`
  background-color: ${p => p.theme.colors.white};
  z-index: 20 !important;
  width: auto;
  border-radius: 4px;
  box-shadow: rgb(15 15 15 / 5%) 0 0 0 1px, rgb(15 15 15 / 10%) 0 3px 6px, rgb(15 15 15 / 20%) 0 9px 24px;
`;

const StyledInsertRoot = styled(FloatingLink.InsertRoot)`
  ${CSSFloatingRoot}
`;

const StyledEditRoot = styled(FloatingLink.EditRoot)`
  ${CSSFloatingRoot}
`;

const StyledFloatingRow = styled.div`
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const CSSFloatingInput = css`
  border-style: none;
  background-color: ${p => p.theme.colors.white};
  height: 2rem;
  flex-grow: 1;
  padding: 0px;
  line-height: 20px;

  &:focus {
    outline: 2px solid transparent;
    outline-offset: 2px;
  }
`;

const StyledFloatingUrlInput = styled(FloatingLink.UrlInput)`
  ${CSSFloatingInput}
`;

const StyledFloatingTextInput = styled(FloatingLink.TextInput)`
  ${CSSFloatingInput}
`;
